import React from 'react';

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

interface LoadingMetrics {
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  error?: string;
  itemCount?: number;
  cacheHit?: boolean;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private loadingMetrics = new Map<string, LoadingMetrics>();
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initializeObservers();
  }

  private initializeObservers() {
    if (typeof window === 'undefined') return;

    try {
      // Observe navigation timing
      if (PerformanceObserver.supportedEntryTypes.includes('navigation')) {
        const navObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.recordMetric('page_load_time', navEntry.loadEventEnd - navEntry.loadEventStart);
              this.recordMetric('dom_content_loaded', navEntry.domContentLoadedEventEnd - navEntry.domContentLoadedEventStart);
              this.recordMetric('first_paint', navEntry.responseEnd - navEntry.requestStart);
            }
          });
        });
        navObserver.observe({ entryTypes: ['navigation'] });
        this.observers.push(navObserver);
      }

      // Observe resource timing (for API calls)
      if (PerformanceObserver.supportedEntryTypes.includes('resource')) {
        const resourceObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.name.includes('/audience') || entry.name.includes('/simulation')) {
              this.recordMetric('api_request_duration', entry.duration, {
                url: entry.name,
                responseTime: entry.duration
              });
            }
          });
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);
      }

      // Observe largest contentful paint
      if (PerformanceObserver.supportedEntryTypes.includes('largest-contentful-paint')) {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          if (entries.length > 0) {
            const lcp = entries[entries.length - 1];
            this.recordMetric('largest_contentful_paint', lcp.startTime);
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'], buffered: true });
        this.observers.push(lcpObserver);
      }
    } catch (error) {
      console.warn('Performance monitoring setup failed:', error);
    }
  }

  recordMetric(name: string, value: number, metadata?: Record<string, any>) {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      metadata
    };
    
    this.metrics.push(metric);
    
    // Keep only last 1000 metrics to prevent memory leaks
    if (this.metrics.length > 1000) {
      this.metrics = this.metrics.slice(-1000);
    }

    // Send to analytics if available
    this.sendToAnalytics(metric);
  }

  startLoading(operationId: string, metadata?: Record<string, any>) {
    this.loadingMetrics.set(operationId, {
      startTime: Date.now(),
      success: false,
      ...metadata
    });
  }

  endLoading(operationId: string, success: boolean, metadata?: Record<string, any>) {
    const loadingMetric = this.loadingMetrics.get(operationId);
    if (!loadingMetric) return;

    const endTime = Date.now();
    const duration = endTime - loadingMetric.startTime;

    const completedMetric = {
      ...loadingMetric,
      endTime,
      duration,
      success,
      ...metadata
    };

    this.loadingMetrics.set(operationId, completedMetric);

    // Record the metric
    this.recordMetric(`${operationId}_duration`, duration, {
      success,
      ...metadata
    });

    // Clean up old metrics
    setTimeout(() => {
      this.loadingMetrics.delete(operationId);
    }, 30000); // Keep for 30 seconds for potential analysis
  }

  recordError(operationId: string, error: string | Error, metadata?: Record<string, any>) {
    const errorMessage = typeof error === 'string' ? error : error.message;
    
    this.recordMetric(`${operationId}_error`, 1, {
      error: errorMessage,
      ...metadata
    });

    // Update loading metric if it exists
    const loadingMetric = this.loadingMetrics.get(operationId);
    if (loadingMetric) {
      this.endLoading(operationId, false, { error: errorMessage, ...metadata });
    }
  }

  getMetrics(name?: string): PerformanceMetric[] {
    if (name) {
      return this.metrics.filter(m => m.name === name);
    }
    return [...this.metrics];
  }

  getLoadingMetrics(operationId?: string): Map<string, LoadingMetrics> | LoadingMetrics | undefined {
    if (operationId) {
      return this.loadingMetrics.get(operationId);
    }
    return new Map(this.loadingMetrics);
  }

  getAverageMetric(name: string, timeWindowMs: number = 300000): number | null {
    const cutoffTime = Date.now() - timeWindowMs;
    const relevantMetrics = this.metrics.filter(
      m => m.name === name && m.timestamp >= cutoffTime
    );

    if (relevantMetrics.length === 0) return null;

    const sum = relevantMetrics.reduce((acc, m) => acc + m.value, 0);
    return sum / relevantMetrics.length;
  }

  getMetricStats(name: string): {
    count: number;
    average: number;
    min: number;
    max: number;
    recent: PerformanceMetric[];
  } | null {
    const metrics = this.getMetrics(name);
    if (metrics.length === 0) return null;

    const values = metrics.map(m => m.value);
    const recent = metrics.slice(-10); // Last 10 metrics

    return {
      count: metrics.length,
      average: values.reduce((a, b) => a + b, 0) / values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      recent
    };
  }

  private sendToAnalytics(metric: PerformanceMetric) {
    if (typeof window !== 'undefined' && (window as any).analytics) {
      (window as any).analytics.track('Performance Metric', {
        metric_name: metric.name,
        metric_value: metric.value,
        timestamp: metric.timestamp,
        ...metric.metadata
      });
    }
  }

  // Audience-specific monitoring methods
  measureAudienceLoading(operationId: string, audienceCount?: number) {
    this.startLoading(`audience_${operationId}`, { audienceCount });
  }

  measureAudienceLoadingComplete(operationId: string, success: boolean, audienceCount?: number, cacheHit?: boolean) {
    this.endLoading(`audience_${operationId}`, success, { audienceCount, cacheHit });
  }

  measureCacheHit(operation: string, hitCount: number, totalCount: number) {
    const hitRate = totalCount > 0 ? hitCount / totalCount : 0;
    this.recordMetric(`cache_hit_rate_${operation}`, hitRate, {
      hitCount,
      totalCount,
      operation
    });
  }

  measurePaginationPerformance(page: number, pageSize: number, totalResults: number, loadTime: number) {
    this.recordMetric('pagination_load_time', loadTime, {
      page,
      pageSize,
      totalResults,
      itemsPerMs: totalResults / Math.max(loadTime, 1)
    });
  }

  // Clean up observers
  dispose() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    this.metrics = [];
    this.loadingMetrics.clear();
  }
}

// Create a singleton instance
export const performanceMonitor = new PerformanceMonitor();

// React hook for easy component integration
export const usePerformanceMonitor = () => {
  const measureComponent = (componentName: string) => {
    const startTime = Date.now();
    
    return {
      end: (metadata?: Record<string, any>) => {
        const duration = Date.now() - startTime;
        performanceMonitor.recordMetric(`component_render_${componentName}`, duration, metadata);
      }
    };
  };

  const measureAsyncOperation = async <T>(
    operationName: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> => {
    performanceMonitor.startLoading(operationName, metadata);
    
    try {
      const result = await operation();
      performanceMonitor.endLoading(operationName, true, metadata);
      return result;
    } catch (error) {
      performanceMonitor.recordError(operationName, error as Error, metadata);
      throw error;
    }
  };

  return {
    measureComponent,
    measureAsyncOperation,
    recordMetric: performanceMonitor.recordMetric.bind(performanceMonitor),
    recordError: performanceMonitor.recordError.bind(performanceMonitor)
  };
};

// High-order component for automatic performance monitoring
export const withPerformanceMonitoring = <P extends Record<string, any>>(
  WrappedComponent: React.ComponentType<P>,
  componentName?: string
) => {
  const componentDisplayName = componentName || WrappedComponent.displayName || WrappedComponent.name;
  
  const WithPerformanceMonitoring = React.forwardRef<any, P>((props, ref) => {
    React.useEffect(() => {
      performanceMonitor.recordMetric(`component_mount_${componentDisplayName}`, Date.now());
      
      return () => {
        performanceMonitor.recordMetric(`component_unmount_${componentDisplayName}`, Date.now());
      };
    }, []);

    return React.createElement(WrappedComponent, { ...props, ref } as P);
  });

  WithPerformanceMonitoring.displayName = `WithPerformanceMonitoring(${componentDisplayName})`;
  
  return WithPerformanceMonitoring;
};